import React, { useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import Icon from "@mdi/react";
import { mdiCheckCircleOutline } from "@mdi/js";
import { Button, Modal } from "@heroui/react";
import { contractorDetailStore } from "@/stores/ContractorDetailStore";

import { SmsStep } from "./SmsStep";
import { formatMoneyRub } from "@shared-utils/helpers";

type Step = "confirm" | "sms" | "success";

const PayoutModal: React.FC = observer(() => {
  const store = contractorDetailStore;
  const [step, setStep] = useState<Step>("confirm");

  const contractor = store.contractor;
  // Snapshot the amount at the time the modal opens so it doesn't change mid-flow
  const [amount, setAmount] = useState(0);

  useEffect(() => {
    if (store.po.isModalOpen) {
      setStep("confirm");
      setAmount(store.po.pendingPayout);
    }
  }, [store.po.isModalOpen]);

  const handleClose = () => {
    store.po.closeModal();
  };

  const handleRequestPayout = async () => {
    await store.po.requestPayout();
    if (store.po.sessionId) {
      setStep("sms");
    }
  };

  const handleResend = async () => {
    // Re-request creates a new session and sends a new SMS
    await store.po.requestPayout();
  };

  const handleConfirm = async (code: string) => {
    const ok = await store.po.confirmPayout(code);
    if (ok) {
      setStep("success");
    }
  };

  if (!contractor) return null;

  return (
    <Modal.Backdrop isOpen={store.po.isModalOpen} onOpenChange={handleClose}>
      <Modal.Container size="sm">
        <Modal.Dialog className="pm-modal">
          {step !== "success" && <Modal.CloseTrigger />}

          {step === "confirm" && (
            <>
              <Modal.Header className="pm-modal__header">
                <Modal.Heading className="pm-modal__heading">
                  Выплата контрагенту
                </Modal.Heading>
                <div className="pm-total">
                  <span className="pm-total__label">Итого к выплате</span>
                  <span className="pm-total__amount">
                    {formatMoneyRub(amount)}
                  </span>
                </div>
              </Modal.Header>
              <Modal.Body className="pm-modal__body">
                <div className="pm-recipients">
                  <div className="pm-recipient">
                    <div className="pm-recipient__info">
                      <span className="pm-recipient__name">
                        {contractor.name}
                      </span>
                      <span className="pm-recipient__address">
                        {contractor.address}
                      </span>
                    </div>
                    <span className="pm-recipient__amount">
                      {formatMoneyRub(amount)}
                    </span>
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer className="pm-modal__footer">
                <Button variant="tertiary" onPress={handleClose}>
                  Отмена
                </Button>
                <Button
                  variant="primary"
                  isPending={store.po.isRequesting}
                  onPress={handleRequestPayout}
                >
                  Продолжить
                </Button>
              </Modal.Footer>
            </>
          )}

          {step === "sms" && (
            <SmsStep
              onBack={() => setStep("confirm")}
              onResend={handleResend}
              onSubmit={handleConfirm}
              isConfirming={store.po.isConfirming}
              isResending={store.po.isRequesting}
            />
          )}

          {step === "success" && (
            <>
              <Modal.Body className="pm-modal__body pm-modal__body--success">
                <div className="pm-success">
                  <div className="pm-success__icon">
                    <Icon path={mdiCheckCircleOutline} size={2.5} />
                  </div>
                  <span className="pm-success__title">Выплата выполнена</span>
                  <span className="pm-success__amount">
                    {formatMoneyRub(amount)}
                  </span>
                  <span className="pm-success__hint">
                    Переведено: {contractor.name}
                  </span>
                </div>
              </Modal.Body>
              <Modal.Footer className="pm-modal__footer pm-modal__footer--center">
                <Button variant="primary" onPress={handleClose}>
                  Готово
                </Button>
              </Modal.Footer>
            </>
          )}
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
});

PayoutModal.displayName = "PayoutModal";
export default PayoutModal;
